import 'react-native-url-polyfill/auto';
import { createClient, processLock } from '@supabase/supabase-js';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { AppState, Platform } from 'react-native';
import { env, config, isDevelopment } from '../config/environment';

// Enhanced storage implementation with fallback strategies and Node.js support
const createReactNativeStorage = () => {
  const isWeb = Platform.OS === 'web';
  const isNative = Platform.OS === 'ios' || Platform.OS === 'android';
  const isNode = typeof window === 'undefined';

  // In-memory storage fallback for Node.js/server environments
  const memoryStorage = new Map<string, string>();

  return {
    getItem: async (key: string): Promise<string | null> => {
      try {
        if (isNode) {
          // Node.js environment - use in-memory storage
          return memoryStorage.get(key) || null;
        } else if (isWeb) {
          // Web platform - try localStorage first, then sessionStorage
          if (typeof window !== 'undefined') {
            if (window.localStorage) {
              return window.localStorage.getItem(key);
            } else if (window.sessionStorage) {
              console.warn('⚠️ localStorage unavailable, using sessionStorage');
              return window.sessionStorage.getItem(key);
            }
          }
          console.warn('⚠️ No web storage available, using memory fallback');
          return memoryStorage.get(key) || null;
        } else if (isNative) {
          // Native platforms - use AsyncStorage
          return await AsyncStorage.getItem(key);
        } else {
          // Fallback for other platforms
          console.warn('⚠️ Unsupported platform for storage, using memory fallback');
          return memoryStorage.get(key) || null;
        }
      } catch (error) {
        console.error('🚨 Storage getItem error, using memory fallback:', error);
        return memoryStorage.get(key) || null;
      }
    },

    setItem: async (key: string, value: string): Promise<void> => {
      try {
        if (isNode) {
          // Node.js environment - use in-memory storage
          memoryStorage.set(key, value);
          return;
        } else if (isWeb) {
          // Web platform - try localStorage first, then sessionStorage
          if (typeof window !== 'undefined') {
            if (window.localStorage) {
              window.localStorage.setItem(key, value);
              return;
            } else if (window.sessionStorage) {
              console.warn('⚠️ localStorage unavailable, using sessionStorage');
              window.sessionStorage.setItem(key, value);
              return;
            }
          }
          console.warn('⚠️ No web storage available, using memory fallback');
          memoryStorage.set(key, value);
        } else if (isNative) {
          // Native platforms - use AsyncStorage
          await AsyncStorage.setItem(key, value);
        } else {
          // Fallback for other platforms
          console.warn('⚠️ Unsupported platform for storage setItem, using memory fallback');
          memoryStorage.set(key, value);
        }
      } catch (error) {
        console.error('🚨 Storage setItem error, using memory fallback:', error);
        memoryStorage.set(key, value);
      }
    },

    removeItem: async (key: string): Promise<void> => {
      try {
        if (isNode) {
          // Node.js environment - use in-memory storage
          memoryStorage.delete(key);
          return;
        } else if (isWeb) {
          // Web platform - try localStorage first, then sessionStorage
          if (typeof window !== 'undefined') {
            if (window.localStorage) {
              window.localStorage.removeItem(key);
              return;
            } else if (window.sessionStorage) {
              console.warn('⚠️ localStorage unavailable, using sessionStorage');
              window.sessionStorage.removeItem(key);
              return;
            }
          }
          console.warn('⚠️ No web storage available, using memory fallback');
          memoryStorage.delete(key);
        } else if (isNative) {
          // Native platforms - use AsyncStorage
          await AsyncStorage.removeItem(key);
        } else {
          // Fallback for other platforms
          console.warn('⚠️ Unsupported platform for storage removeItem, using memory fallback');
          memoryStorage.delete(key);
        }
      } catch (error) {
        console.error('🚨 Storage removeItem error, using memory fallback:', error);
        memoryStorage.delete(key);
      }
    },
  };
};

// Enhanced singleton pattern to prevent multiple instances
let supabaseInstance: any = null;
let isCreatingClient = false;

const createSupabaseClient = () => {
  if (supabaseInstance) {
    console.log('🔄 Reusing existing Supabase client instance');
    return supabaseInstance;
  }

  if (isCreatingClient) {
    console.log('⏳ Client creation in progress, waiting...');
    // Return a promise that resolves when the client is ready
    return new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        if (supabaseInstance) {
          clearInterval(checkInterval);
          resolve(supabaseInstance);
        }
      }, 10);
    });
  }

  isCreatingClient = true;

  try {
    console.log('🔧 Creating new Supabase client instance');
    supabaseInstance = createClient(
      env.supabaseUrl,
      env.supabaseAnonKey, // ANON key for client-side authentication and RLS
      {
        auth: {
          storage: createReactNativeStorage(),
          autoRefreshToken: true,
          persistSession: true,
          detectSessionInUrl: Platform.OS === 'web' && typeof window !== 'undefined',
          flowType: 'pkce', // Modern PKCE flow for better security
          storageKey: 'padrinho-elo-auth', // Consistent storage key for session persistence
          // React Native has no `navigator.locks`, so auth-js silently falls back
          // to a no-op lock — every auth.* call (getSession / refreshSession /
          // setSession / the auto-refresh tick) runs CONCURRENTLY with no
          // serialization. When Face ID backgrounds→foregrounds the app, the
          // foreground auto-refresh tick + NotificationContext's refresh race the
          // biometric setSession() and trample each other on refresh-token
          // rotation, so setSession() never resolves and biometric login hangs.
          // processLock is Supabase's official RN fix: it serialises all auth.*
          // calls through one in-process mutex. See docs: supabase.com/docs guide
          // for React Native (lock: processLock).
          // Native only: on web we leave `lock` undefined so auth-js keeps using
          // the built-in navigatorLock (navigator.locks) for cross-tab session
          // coordination — a provided lock would override that.
          lock: Platform.OS === 'web' ? undefined : processLock,
        },
        global: {
          headers: {
            'X-Client-Info': 'padrinho-elo@1.0.0',
            'Connection': 'keep-alive',
            'Cache-Control': 'no-cache',
          },
        },
        realtime: {
          params: {
            eventsPerSecond: 10,
          },
        },
        db: {
          schema: 'public',
        },
      }
    );

    console.log('✅ Supabase client created successfully with unique storage key');
    isCreatingClient = false;
    return supabaseInstance;
  } catch (error) {
    console.error('❌ Failed to create Supabase client:', error);
    isCreatingClient = false;
    throw error;
  }
};

// Initialize Supabase client with ANON key (safe for client-side with RLS)
export const supabase = createSupabaseClient();

// T001 FIX: AppState listener for auto-refresh on native platforms
// On iOS/Android, JS timers are suspended when the app is backgrounded, which
// kills the SDK's internal refresh timer. Without this, access tokens expire
// silently while the app is backgrounded and the user gets logged out.
// Official Supabase recommendation for React Native:
// https://supabase.com/docs/reference/javascript/auth-startautorefresh
if (Platform.OS === 'ios' || Platform.OS === 'android') {
  AppState.addEventListener('change', (state) => {
    if (state === 'active') {
      // App came to foreground — restart the SDK's refresh timer
      supabase.auth.startAutoRefresh();
      console.log('🔄 Supabase: Auto-refresh started (app active)');
    } else {
      // App went to background — pause the SDK's refresh timer to save resources
      supabase.auth.stopAutoRefresh();
      console.log('⏸️ Supabase: Auto-refresh stopped (app backgrounded)');
    }
  });
  console.log('✅ Supabase: AppState listener registered for token auto-refresh');
}

// Enhanced logging for debugging
if (config.logging.enabled) {
  const isNode = typeof window === 'undefined';
  const storageType = isNode 
    ? 'Memory (Node.js)' 
    : Platform.OS === 'web' 
      ? 'localStorage/sessionStorage' 
      : 'AsyncStorage';

  const isAnonKey = env.supabaseAnonKey.startsWith('eyJ');
  console.log('🔧 Supabase Client Configuration:');
  console.log('- Platform:', Platform.OS);
  console.log('- Environment:', isNode ? 'Node.js/Server' : 'Client');
  console.log('- URL:', env.supabaseUrl);
  console.log('- Client Key Type:', isAnonKey ? 'ANON/JWT (Client-side with RLS)' : 'Unknown');
  console.log('- Client Key Preview:', env.supabaseAnonKey.substring(0, 20) + '...');
  console.log('- Storage Type:', storageType);
  console.log('- PKCE Flow: ✅ Enabled');
  console.log('- Auto Refresh: ✅ Enabled');
  console.log('- Persist Session: ✅ Enabled');
  console.log('- Session URL Detection:', Platform.OS === 'web' && typeof window !== 'undefined' ? '✅ Enabled' : '❌ Disabled');
  console.log('- Debug Mode:', config.logging.supabaseDebug ? '✅ Enabled' : '❌ Disabled');
  console.log('- Client Privileges: ❌ Limited (RLS enforced)');
  console.log('- Timeout:', config.api.timeout + 'ms');

  // Validate the key
  if (isAnonKey) {
    try {
      const keyParts = env.supabaseAnonKey.split('.');
      if (keyParts[1]) {
        const payload = JSON.parse(atob(keyParts[1]));
        const expiration = new Date(payload.exp * 1000);
        const now = new Date();
        console.log('- JWT Expiration:', expiration.toISOString());
        console.log('- JWT Valid:', now < expiration ? '✅ Yes' : '❌ EXPIRED');
        console.log('- JWT Project:', payload.ref || 'Unknown');
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('❌ JWT validation failed:', errorMessage);
    }
  }
}

// Simplified validation function - fast and non-blocking
const validateConfiguration = async (): Promise<boolean> => {
  try {
    console.log('🔍 Validating Supabase configuration...');

    // Quick 5-second timeout - validation should not block the app
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Validation timeout')), 5000)
    );

    // Use auth session check instead of profiles table to avoid RLS issues
    const connectivityPromise = supabase.auth.getSession();

    const result = await Promise.race([connectivityPromise, timeoutPromise]) as any;
    const { error } = result;

    if (error && !error.message?.includes('session') && !error.message?.includes('JWT')) {
      console.warn('⚠️ Supabase validation warning:', error.message);
      // Don't fail - app can still work, just log the warning
      return true;
    }
    
    console.log('✅ Supabase configuration validated successfully');
    return true;
  } catch (error) {
    // Always return true - validation timeout should NOT block the app
    // The app functionality doesn't depend on this validation passing
    console.warn('⚠️ Supabase validation skipped (timeout) - app should work normally');
    return true;
  }
};

// SECURITY: Service role client REMOVED from client code for security
// Service role operations must only happen server-side via API endpoints

// Prevent multiple concurrent validations
let validationPromise: Promise<boolean> | null = null;

// Export validation function for use in app initialization
export const validateSupabaseConnection = () => {
  if (validationPromise) {
    console.log('🔄 Validation already in progress, reusing existing promise...');
    return validationPromise;
  }

  validationPromise = validateConfiguration().finally(() => {
    // Reset after completion to allow future validations
    setTimeout(() => {
      validationPromise = null;
    }, 30000); // Reset after 30 seconds
  });

  return validationPromise;
};

// Connection health check
export const checkConnectionHealth = async (): Promise<{
  isHealthy: boolean;
  latency: number;
  error?: string;
}> => {
  const startTime = Date.now();

  try {
    const { error } = await supabase.auth.getSession();
    const latency = Date.now() - startTime;

    if (error) {
      return {
        isHealthy: false,
        latency,
        error: error.message,
      };
    }

    return {
      isHealthy: true,
      latency,
    };
  } catch (error) {
    const latency = Date.now() - startTime;
    return {
      isHealthy: false,
      latency,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

// Declare global type for validation flag
declare global {
  var __supabaseValidated: boolean | undefined;
}

// Run validation only once in development
if (isDevelopment && !global.__supabaseValidated) {
  global.__supabaseValidated = true;
  validateConfiguration().then((isValid) => {
    if (isValid) {
      console.log('🎉 Supabase initialization completed successfully');
    } else {
      console.error('⚠️ Supabase initialization completed with warnings - some features may not work correctly');
    }
  }).catch(error => {
    console.error('❌ Failed to validate Supabase configuration during initialization:', error);
    console.log('💡 Suggestions:');
    console.log('   1. Check your environment variables in Replit Secrets');
    console.log('   2. Verify your Supabase project is active');
    console.log('   3. Ensure your API keys haven\'t expired');
    console.log('   4. Check your internet connection');
  });
}
// Enhanced singleton pattern with proper client management
export const getSupabaseClient = () => {
  return supabase;
};